import moment from "moment";
import { TableHeader } from "../table";

/**
 * Orders table headers by their `order` field.
 *
 * @param headers - Unsorted header definitions
 * @returns Headers sorted ascending by `order`
 */
export function sortTableHeadersByOrder(headers: TableHeader[]) {
  return [...headers].sort((a, b) => a.order - b.order);
}

/**
 * Sorts table data by a specified column key and updates table state.
 *
 * @param key - Primary key of the column to sort by
 * @param subKey - Optional nested key for sorting by a sub-property
 * @param tableDataToRender - Array of table row data to sort
 * @param sortAscending - Ascending when true
 * @param setTableDataToRender - State setter for sorted data
 * @param startIndex - Reserved pagination start index
 * @param endIndex - Reserved pagination end index
 * @param type - Optional sort type: "date" or auto numeric/string
 */
export const sortTableData = (
  key: string,
  subKey: string | undefined,
  tableDataToRender: any[],
  sortAscending: boolean,
  setTableDataToRender: React.Dispatch<React.SetStateAction<any[]>>,
  startIndex: number,
  endIndex: number,
  type?: string,
) => {
  void startIndex;
  void endIndex;

  const sortedArrayData = [...tableDataToRender].sort((a, b) => {
    const getValue = (item: any) => (subKey ? item[key][subKey] : item[key]);
    const valA = getValue(a);
    const valB = getValue(b);

    if (type === "date") {
      const dateA = parseMDYString(moment(valA).format("MM/DD/YYYY"));
      const dateB = parseMDYString(moment(valB).format("MM/DD/YYYY"));
      return sortAscending
        ? dateA.getTime() - dateB.getTime()
        : dateB.getTime() - dateA.getTime();
    }

    const isNumeric = (val: any) => /^-?\d+(\.\d+)?$/.test(val);

    if (isNumeric(valA) && isNumeric(valB)) {
      return sortAscending
        ? Number(valA) - Number(valB)
        : Number(valB) - Number(valA);
    }

    return sortAscending
      ? String(valA).localeCompare(String(valB))
      : String(valB).localeCompare(String(valA));
  });

  setTableDataToRender([...sortedArrayData]);
};

function parseMDYString(dateStr: string): Date {
  const [month, day, year] = dateStr.split("/").map(Number);
  return new Date(year, month - 1, day);
}
